import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Briefcase, Download, FilePlus2, Trash2, Save,
  FileText, CheckCircle2, Circle, Search, Plus, Users, Package,
} from "lucide-react";
import { maskPhone } from "@/lib/format";

const SHARED_FIELDS = [
  ["full_name", "Full name"], ["email", "Email"], ["phone", "Phone"],
  ["address_line", "Street address"], ["city", "City"], ["state", "State"],
  ["zip_code", "ZIP code"], ["county", "County"], ["case_number", "Case / file number"],
  ["attorney_name", "Attorney name"], ["bar_number", "State bar number"], ["firm", "Law firm"],
];

export default function MatterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [matter, setMatter] = useState(null);
  const [shared, setShared] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [savingShared, setSavingShared] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState("");

  const load = useCallback(() => {
    api.get(`/matters/${id}`).then(({ data }) => { setMatter(data); setShared(data.shared_data || {}); }).catch(() => setNotFound(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!addOpen) return;
    const h = setTimeout(() => {
      setSearching(true);
      api.get("/forms", { params: { q: query || undefined, page_size: 20 } })
        .then(({ data }) => setResults(data.items || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(h);
  }, [query, addOpen]);

  const saveShared = async () => {
    setSavingShared(true);
    try {
      const { data } = await api.put(`/matters/${id}`, {
        name: matter.name, matter_type: matter.matter_type, county: matter.county,
        case_number: matter.case_number, description: matter.description, shared_data: shared,
      });
      setMatter((m) => ({ ...m, ...data }));
      toast.success(t("matter.sharedSaved"));
    } catch { toast.error(t("matter.saveFail")); }
    setSavingShared(false);
  };

  const addForm = async (num) => {
    setAdding(num);
    try {
      const { data } = await api.post(`/matters/${id}/forms`, { form_number: num });
      setMatter((m) => {
        const exists = (m.submissions || []).some((s) => s.id === data.id);
        return { ...m, submissions: exists ? m.submissions : [data, ...(m.submissions || [])] };
      });
      toast.success(t("matter.formAdded", { number: num }));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("matter.addFail"));
    }
    setAdding("");
  };

  const removeForm = async (subId) => {
    try {
      await api.patch(`/submissions/${subId}/matter`, { matter_id: null });
      setMatter((m) => ({ ...m, submissions: m.submissions.filter((s) => s.id !== subId) }));
      toast.success(t("matter.formRemoved"));
    } catch { toast.error(t("matter.removeFail")); }
  };

  const download = async (s) => {
    try {
      const res = await api.get(`/submissions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${s.form_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("matter.downloadFail")); }
  };

  const [downloadingPacket, setDownloadingPacket] = useState(false);
  const downloadPacket = async () => {
    setDownloadingPacket(true);
    try {
      const res = await api.get(`/matters/${id}/packet`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${(matter.name || "case").replace(/[^A-Za-z0-9]+/g, "-")}-packet.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("matter.packetReady"));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("matter.packetFail"));
    }
    setDownloadingPacket(false);
  };

  const deleteMatter = async (deleteForms) => {
    try {
      await api.delete(`/matters/${id}`, { params: { delete_forms: deleteForms } });
      toast.success(t("matter.deleted"));
      navigate("/matters");
    } catch { toast.error(t("matter.deleteFail")); }
  };

  if (notFound) return (<div><Navbar /><div className="max-w-3xl mx-auto px-6 py-32 text-center"><h1 className="font-serif text-4xl mb-4">{t("matter.notFound")}</h1><Link to="/matters"><Button variant="outline" className="rounded-sm">{t("matter.back")}</Button></Link></div><Footer /></div>);
  if (!matter) return <div className="min-h-screen grid place-items-center text-muted-foreground">{t("matters.loading")}</div>;

  const subs = matter.submissions || [];

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-24 min-h-[60vh]">
        <Link to="/matters" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={16} /> {t("matter.allMatters")}</Link>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="grid place-items-center w-11 h-11 bg-primary text-primary-foreground"><Briefcase size={20} /></span>
              <Badge className="rounded-sm bg-primary">{matter.matter_type}</Badge>
            </div>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl tracking-tight mb-2" data-testid="matter-name">{matter.name}</h1>
            {matter.county && <p className="text-muted-foreground">{matter.county} County</p>}
          </div>
          <div className="flex items-center gap-3">
            {subs.length > 0 && (
              <Button onClick={downloadPacket} disabled={downloadingPacket} data-testid="download-packet-btn" className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <Package size={16} /> {downloadingPacket ? t("matter.buildingPacket") : t("matter.downloadPacket")}
              </Button>
            )}
            <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="delete-matter-btn" className="rounded-sm gap-2 text-muted-foreground hover:text-accent"><Trash2 size={16} /> {t("matter.delete")}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("matter.deleteTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("matter.deleteDesc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel data-testid="delete-matter-cancel">{t("matter.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMatter(false)} data-testid="delete-matter-keep" className="bg-primary">{t("matter.deleteKeepForms")}</AlertDialogAction>
                <AlertDialogAction onClick={() => deleteMatter(true)} data-testid="delete-matter-all" className="bg-accent text-accent-foreground hover:bg-accent/90">{t("matter.deleteAll")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Shared info */}
          <div className="lg:col-span-1">
            <div className="grid-card p-7 lg:sticky lg:top-24" data-testid="matter-shared">
              <div className="flex items-center gap-2 mb-1"><Users size={18} className="text-accent" /><p className="overline text-accent">{t("matter.sharedEyebrow")}</p></div>
              <p className="text-sm text-muted-foreground mb-5">{t("matter.sharedDesc")}</p>
              <div className="space-y-3">
                {SHARED_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-xs text-muted-foreground">{label}</Label>
                    <Input data-testid={`shared-${key}`} value={key === "phone" ? maskPhone(shared[key] || "") : (shared[key] || "")} onChange={(e) => setShared((s) => ({ ...s, [key]: key === "phone" ? maskPhone(e.target.value) : e.target.value }))} className="rounded-sm mt-1 h-9" />
                  </div>
                ))}
              </div>
              <Button onClick={saveShared} disabled={savingShared} data-testid="save-shared-btn" className="w-full rounded-sm gap-2 mt-5 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                <Save size={16} /> {savingShared ? t("matter.saving") : t("matter.saveShared")}
              </Button>
            </div>
          </div>

          {/* Forms in matter */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif font-bold text-2xl tracking-tight">{t("matter.formsInCase", { count: subs.length })}</h2>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-form-btn" className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-accent/90"><FilePlus2 size={16} /> {t("matter.addForm")}</Button>
                </DialogTrigger>
                <DialogContent data-testid="add-form-dialog" className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-serif text-2xl">{t("matter.addFormTitle")}</DialogTitle></DialogHeader>
                  <div className="relative mb-2">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="form-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("matter.searchForms")} className="rounded-sm pl-9" autoFocus />
                  </div>
                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {searching && <p className="text-sm text-muted-foreground py-4 text-center">{t("matter.searching")}</p>}
                    {!searching && results.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t("matter.noResults")}</p>}
                    {results.map((f) => (
                      <div key={f.number} data-testid={`search-result-${f.number}`} className="flex items-center gap-3 p-3 border-2 border-black">
                        <div className="flex-1 min-w-0">
                          <Badge variant="outline" className="rounded-sm font-mono text-xs mb-1">{f.number}</Badge>
                          <p className="text-sm truncate">{f.title}</p>
                        </div>
                        <Button size="sm" onClick={() => addForm(f.number)} disabled={adding === f.number} data-testid={`add-result-${f.number}`} className="rounded-sm gap-1 shrink-0 bg-primary hover:bg-accent hover:text-accent-foreground">
                          <Plus size={14} /> {adding === f.number ? t("matter.adding") : t("matter.add")}
                        </Button>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {subs.length === 0 ? (
              <div className="grid-card p-12 text-center" data-testid="matter-forms-empty">
                <FileText className="mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-4">{t("matter.formsEmpty")}</p>
                <Button onClick={() => setAddOpen(true)} className="rounded-sm gap-2 bg-accent text-accent-foreground"><FilePlus2 size={16} /> {t("matter.addForm")}</Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {subs.map((s) => (
                  <div key={s.id} className="grid-card p-5 flex flex-col" data-testid={`matter-form-${s.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="rounded-sm font-mono text-xs">{s.form_number}</Badge>
                      <button onClick={() => removeForm(s.id)} data-testid={`remove-form-${s.id}`} className="text-muted-foreground hover:text-accent transition-colors"><Trash2 size={15} /></button>
                    </div>
                    <h3 className="font-serif text-base leading-tight mb-2 flex-1">{s.title}</h3>
                    {s.filing_status === "filed" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-3"><CheckCircle2 size={14} /> {t("saved.filedBadge")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-3"><Circle size={14} /> {t("saved.notFiledBadge")}</span>
                    )}
                    <div className="flex gap-2">
                      <Link to={`/forms/${s.form_number}?submission=${s.id}`} className="flex-1" data-testid={`open-matter-form-${s.id}`}>
                        <Button variant="outline" className="w-full rounded-sm gap-2 hover:bg-secondary text-sm">{t("matter.openFill")} <ArrowRight size={14} /></Button>
                      </Link>
                      <Button onClick={() => download(s)} data-testid={`download-matter-form-${s.id}`} className="rounded-sm bg-primary hover:bg-accent hover:text-accent-foreground transition-colors"><Download size={16} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
